import { router as inertiaRouter } from '@inertiajs/react'
import type { Errors, Page, PageProps, RequestPayload, VisitOptions } from '@inertiajs/core'

export function inertiaErrorMessage(errors: unknown, fallback = 'Unable to save your changes.') {
  const values: string[] = []

  const collect = (value: unknown) => {
    if (typeof value === 'string' && value.trim()) {
      values.push(value.trim())
      return
    }

    if (Array.isArray(value)) {
      value.forEach(collect)
      return
    }

    if (value && typeof value === 'object') Object.values(value).forEach(collect)
  }

  collect(errors)
  return values.length > 0 ? Array.from(new Set(values)).join(', ') : fallback
}

export type ChartMutationMethod = 'post' | 'put' | 'patch' | 'delete'

export interface ChartMutation {
  key: string
  url: string
  method: ChartMutationMethod
  data?: Record<string, unknown>
  /** Replace a waiting snapshot for this key instead of submitting twice. */
  replacePending?: boolean
  /** This successful mutation leaves the chart page (archive/duplicate/delete). */
  terminal?: boolean
  /**
   * The page this very request answered with. Read anything that belongs to
   * THIS response off it — a flash id, the saved rows — rather than off the
   * component's latest props: several writes can be queued at once, and the
   * render carrying one response's props can land after the next response's
   * callback has already run.
   */
  onSuccess?: (page?: Page<PageProps>) => void
  onError?: (errors: unknown) => void
  onCancel?: () => void
  onFinish?: () => void
}

type ChartMutationRouter = {
  post: (url: string, data?: RequestPayload, options?: VisitOptions) => void
  put: (url: string, data?: RequestPayload, options?: VisitOptions) => void
  patch: (url: string, data?: RequestPayload, options?: VisitOptions) => void
  delete: (url: string, options?: VisitOptions) => void
  on?: (event: 'start', callback: (event: { detail?: { visit?: { url?: string | URL } } }) => void) => () => void
}

export interface ChartMutationQueue {
  enqueue: (item: ChartMutation) => boolean
  onForeignVisit: (listener: () => void) => () => void
  onTerminalSuccess: (listener: () => void) => () => void
  dispose: () => void
}

interface PendingMutation extends ChartMutation {}

const visitPath = (value: string | URL) => {
  try {
    const parsed = new URL(String(value), 'http://inertia.local')
    return `${parsed.pathname}${parsed.search}`
  } catch {
    return String(value)
  }
}

/**
 * A page-scoped mutation queue. Inertia cancels its active visit whenever a
 * new visit starts, so every chart write must go through this one queue.
 * Waiting snapshots can be replaced, but only one request is ever in flight.
 */
export function createChartMutationQueue({
  router = inertiaRouter as ChartMutationRouter,
}: {
  router?: ChartMutationRouter
} = {}): ChartMutationQueue {
  const waiting: PendingMutation[] = []
  const foreignVisitListeners = new Set<() => void>()
  const terminalSuccessListeners = new Set<() => void>()
  let active: PendingMutation | null = null
  let disposed = false
  let startingOwnVisit = false

  const notifyCancelled = (item: PendingMutation) => {
    item.onCancel?.()
    if (!item.onCancel) item.onError?.({ base: 'This change was interrupted by navigation.' })
    item.onFinish?.()
  }

  const drain = () => {
    waiting.splice(0).forEach(notifyCancelled)
  }

  const pump = () => {
    if (disposed || active || waiting.length === 0) return
    const item = waiting.shift()!
    active = item
    let resolved = false
    let cancelled = false

    const options: VisitOptions = {
      preserveScroll: true,
      preserveState: true,
      onSuccess: page => {
        resolved = true
        if (item.terminal) {
          // A successful terminal action normally redirects away before this
          // component can unmount. Stop the old queue first so waiting writes
          // cannot start during that redirect.
          disposed = true
          terminalSuccessListeners.forEach(listener => listener())
          removeStartListener()
          drain()
          terminalSuccessListeners.clear()
          foreignVisitListeners.clear()
        }
        item.onSuccess?.(page)
      },
      onError: errors => {
        resolved = true
        item.onError?.(errors)
      },
      onCancel: () => {
        resolved = true
        cancelled = true
        notifyCancelled(item)
      },
      onFinish: () => {
        if (!resolved) {
          // Inertia normally calls onSuccess/onError before onFinish. Treat a
          // finish without either callback as a completed visit as well; this
          // also keeps the queue compatible with lightweight router doubles.
          item.onFinish?.()
        } else if (cancelled) {
          // onCancel already notified this item.
        } else {
          item.onFinish?.()
        }

        active = null
        if (cancelled || disposed) drain()
        else pump()
      },
    }

    startingOwnVisit = true
    try {
      if (item.method === 'delete') {
        router.delete(item.url, options)
      } else {
        const data = (item.data || {}) as RequestPayload
        router[item.method](item.url, data, options)
      }
    } finally {
      startingOwnVisit = false
    }
  }

  const removeStartListener = router.on?.('start', event => {
    const visit = event.detail?.visit?.url
    const activeIsThisVisit = active && visit !== undefined && visitPath(visit) === visitPath(active.url)
    if (startingOwnVisit || activeIsThisVisit || disposed) return

    foreignVisitListeners.forEach(listener => listener())
    if (active || waiting.length > 0) drain()
  }) || (() => {})

  return {
    enqueue(item) {
      if (disposed) return false
      if (active?.key === item.key && !item.replacePending) return false
      const existingIndex = waiting.findIndex(candidate => candidate.key === item.key)
      if (existingIndex >= 0) {
        if (!item.replacePending) return false
        waiting[existingIndex] = item
        return true
      }

      waiting.push(item)
      pump()
      return true
    },

    onForeignVisit(listener) {
      if (disposed) return () => {}
      foreignVisitListeners.add(listener)
      return () => foreignVisitListeners.delete(listener)
    },

    onTerminalSuccess(listener) {
      if (disposed) return () => {}
      terminalSuccessListeners.add(listener)
      return () => terminalSuccessListeners.delete(listener)
    },

    dispose() {
      if (disposed) return
      disposed = true
      removeStartListener()
      drain()
      foreignVisitListeners.clear()
      terminalSuccessListeners.clear()
    },
  }
}

interface SerializedCollapsedStateSaverOptions {
  queue?: ChartMutationQueue
  router?: ChartMutationRouter
  url: string
  delay?: number
  onError?: (errors: Errors) => void
}

/**
 * Debounces collapse snapshots before handing them to the page mutation
 * queue. A foreign visit clears the timer before it can cancel that visit.
 */
export function createSerializedCollapsedStateSaver({
  queue,
  router,
  url,
  delay = 500,
  onError,
}: SerializedCollapsedStateSaverOptions) {
  const ownedQueue = queue || createChartMutationQueue({ router })
  let timer: ReturnType<typeof setTimeout> | null = null
  let pending: number[] | null = null
  let disposed = false

  const clearPending = () => {
    if (timer !== null) {
      clearTimeout(timer)
      timer = null
    }
    if (pending !== null) {
      pending = null
      onError?.({ base: 'Chart display preference save was interrupted by navigation.' })
    }
  }

  const removeForeignListener = ownedQueue.onForeignVisit(clearPending)
  const removeTerminalListener = ownedQueue.onTerminalSuccess(() => {
    if (timer !== null) clearTimeout(timer)
    timer = null
    pending = null
  })

  const send = () => {
    if (disposed || pending === null) return
    const ids = pending
    pending = null
    ownedQueue.enqueue({
      key: 'chart-display-preferences',
      url,
      method: 'patch',
      data: { collapsed_seat_ids: ids },
      replacePending: true,
      onError: errors => onError?.(errors as Errors),
      onCancel: () => onError?.({ base: 'Chart display preference save was interrupted by navigation.' }),
    })
  }

  return {
    schedule(ids: number[]) {
      if (disposed) return
      pending = Array.from(ids)
      if (timer !== null) clearTimeout(timer)
      timer = setTimeout(() => {
        timer = null
        send()
      }, delay)
    },

    dispose() {
      if (disposed) return
      disposed = true
      pending = null
      if (timer !== null) clearTimeout(timer)
      timer = null
      removeForeignListener()
      removeTerminalListener()
      if (!queue) ownedQueue.dispose()
    },
  }
}